import React from "react";

import { DISTRICTS, districtOfWord } from "../../lib/tokiPonaGarden";
import { SECTIONS, formatPrompt, promptForWord } from "../../lib/tokiPonaPrompts";

/* ============================================================
   kon nimi — lipu mi, the book view. A flat read of everything
   written so far: every word with at least one entry (newest
   activity first), its answers oldest to newest, then the vines
   tying words together.
   ============================================================ */

const districtById = (id) => DISTRICTS.find((d) => d.id === id) || null;

// Fallback caption when a saved prompt id no longer resolves to bespoke or
// generic prompt text — fall back to the section it belongs to.
const sectionLabel = (promptId) => {
  const sectionId = promptId.split("-")[0];
  const section = SECTIONS.find((s) => s.id === sectionId);
  return section ? section.label : "";
};

const formatDate = (at) =>
  new Date(at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const latestAt = (wordEntries) =>
  Math.max(...Object.values(wordEntries).map((e) => e.at || 0));

const BookView = ({ entries, links = [], onOpenWord }) => {
  const words = Object.keys(entries).filter(
    (w) => Object.keys(entries[w] || {}).length > 0
  );
  const sortedWords = [...words].sort(
    (a, b) => latestAt(entries[b]) - latestAt(entries[a])
  );
  const entryCount = words.reduce((sum, w) => sum + Object.keys(entries[w]).length, 0);
  const isEmpty = words.length === 0 && links.length === 0;

  return (
    <div className="kn-book">
      <div className="kn-book-head">
        <h1>lipu mi · my book</h1>
        <p className="kn-book-count">
          {entryCount} entries across {words.length} words, {links.length} vines
        </p>
      </div>

      {isEmpty && (
        <p className="kn-book-empty">
          nothing written yet — the garden is waiting, and there&apos;s no
          hurry.
        </p>
      )}

      {sortedWords.map((word) => {
        const wordEntries = entries[word];
        const district = districtById(districtOfWord(word));
        const promptIds = Object.keys(wordEntries).sort(
          (a, b) => (wordEntries[a].at || 0) - (wordEntries[b].at || 0)
        );
        return (
          <div className="kn-book-word" key={word}>
            <button
              type="button"
              className="kn-book-word-name"
              onClick={() => onOpenWord(word)}
            >
              {word}
            </button>
            {district && (
              <p className="kn-book-word-locator">
                {district.name} · {district.gloss}
              </p>
            )}
            {promptIds.map((promptId) => {
              const entry = wordEntries[promptId];
              const prompt = promptForWord(word, promptId);
              const promptText = prompt ? formatPrompt(prompt.text, word) : sectionLabel(promptId);
              const isLen = promptId.startsWith("len-");
              return (
                <div className="kn-book-entry" key={promptId}>
                  <p className="kn-book-entry-prompt">{promptText}</p>
                  <p className={isLen ? "kn-entry-tp" : "kn-book-entry-text"}>{entry.text}</p>
                  <p className="kn-book-entry-date">{formatDate(entry.at)}</p>
                </div>
              );
            })}
          </div>
        );
      })}

      {links.length > 0 && (
        <div className="kn-book-vines">
          <p className="kn-book-vines-head">linja · the vines</p>
          {links.map((l) => (
            <p className="kn-book-vine-row" key={`${l.from}·${l.to}·${l.at}`}>
              {l.from} ↔ {l.to}
              {l.note ? ` — ${l.note}` : ""}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookView;
